import { useLocation } from "react-router-dom";
import { Mail, MessageCircle, Phone, MapPin, FileText } from "lucide-react";
import { usePublicSiteCms } from "@/lib/siteCms";

const KEY_BY_SLUG = {
  "tentang-kami": "about",
  "syarat-ketentuan": "terms",
  "kebijakan-privasi": "privacy",
  "hubungi-kami": "contact",
};

function renderContent(text) {
  if (!text) return null;
  const blocks = text.split(/\n\s*\n/);
  return blocks.map((block, bi) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const bullets = lines.filter((l) => l.startsWith("- ") || l.startsWith("• "));
    if (bullets.length && bullets.length === lines.length) {
      return (
        <ul key={bi} className="list-disc pl-5 space-y-1.5 text-muted-foreground">
          {lines.map((l, i) => <li key={i}>{l.replace(/^[-•]\s*/, "")}</li>)}
        </ul>
      );
    }
    // Heading pattern like "1. Judul" on its own single line
    if (lines.length === 1 && /^\d+\.\s+\S/.test(lines[0]) && lines[0].length < 60) {
      return <h3 key={bi} className="font-heading text-lg font-semibold text-foreground pt-2">{lines[0]}</h3>;
    }
    return (
      <p key={bi} className="text-muted-foreground leading-relaxed">
        {lines.map((l, i) => (
          <span key={i}>{l}{i < lines.length - 1 && <br />}</span>
        ))}
      </p>
    );
  });
}

export default function InfoPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, "").split("/").pop();
  const cms = usePublicSiteCms();
  const key = KEY_BY_SLUG[slug] || "about";
  const pages = cms?.pages || {};
  const b = cms?.branding || {};

  if (key === "contact") {
    const waDigits = String(b.contact_whatsapp || "").replace(/[^0-9]/g, "").replace(/^0/, "62");
    const items = [
      b.contact_email && { icon: Mail, label: "Email", value: b.contact_email, href: `mailto:${b.contact_email}`, testid: "contact-email" },
      b.contact_whatsapp && { icon: MessageCircle, label: "WhatsApp", value: b.contact_whatsapp, href: waDigits ? `https://wa.me/${waDigits}` : null, testid: "contact-whatsapp" },
      b.contact_phone && { icon: Phone, label: "Telepon", value: b.contact_phone, href: `tel:${String(b.contact_phone).replace(/\s/g, "")}`, testid: "contact-phone" },
      b.contact_address && { icon: MapPin, label: "Alamat", value: b.contact_address, href: null, testid: "contact-address" },
    ].filter(Boolean);

    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="info-page-contact">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Hubungi Kami</h1>
        {pages.contact_intro && <p className="mt-4 text-muted-foreground leading-relaxed">{pages.contact_intro}</p>}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {items.map((it) => {
            const Icon = it.icon;
            const inner = (
              <div className="flex items-start gap-3 bg-card border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors h-full">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary shrink-0"><Icon size={18} /></span>
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">{it.label}</p>
                  <p className="font-medium break-words">{it.value}</p>
                </div>
              </div>
            );
            return it.href ? (
              <a key={it.testid} href={it.href} target="_blank" rel="noreferrer" data-testid={it.testid}>{inner}</a>
            ) : (
              <div key={it.testid} data-testid={it.testid}>{inner}</div>
            );
          })}
        </div>
      </div>
    );
  }

  const page = pages[key] || {};
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid={`info-page-${key}`}>
      <div className="flex items-center gap-2 text-primary mb-3"><FileText size={18} /><span className="text-xs uppercase tracking-widest font-semibold">AVASA</span></div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="info-page-title">{page.title || ""}</h1>
      <div className="mt-8 space-y-4">
        {renderContent(page.content)}
      </div>
    </div>
  );
}
